import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DEFAULT_RINGTONE } from '@avslack/shared';
import { usePhoneContacts } from '../data/phone';
import { useNotificationPrefs } from '../data/notifications';
import { getChannelHuddle } from '../lib/api';
import { startRingtone, stopRingtone } from '../lib/sound';
import { useUsers } from '../data/users';
import { Avatar } from './Avatar';
import { CustomerCard } from './CustomerCard';
import { Icon } from './Icon';
import { closeHuddleWindow } from '../lib/huddle-window';
import { closeCallNotifications } from '../lib/call-notifications';

/**
 * Bejovo hivas a KULON ablakban.
 *
 * A hivott ugyanazt az ablakot latja, amiben a beszelgetes zajlani fog - nincs
 * atvaltas, nincs keresgeles. Az ablak magatol nyilik es elorejon, mert egy
 * hivas eppen akkor a legfontosabb, amikor nem az AVSlack van elotérben.
 *
 * NEM lep be magatol: a mikrofon bekapcsolasa dontes, nem mellekhatas.
 */
export function IncomingCall({
  channelId,
  meId,
  onAccept,
  onDecline,
  variant = 'window',
  onDone,
}: {
  channelId: string;
  meId: string;
  onAccept: () => void;
  onDecline: () => void;
  /**
   * `panel`: a vevoszolgalati lebego panelben (docs/20) - kompakt, NEM vesz el fokuszt
   * (a "Felveszem" nem `autoFocus`), es a vegen nem ablakot zar, hanem `onDone`-t hiv.
   * `bar`: ugyanez az OSSZECSUKOTT panelben - egyetlen sor (nev + ket gomb).
   */
  variant?: 'window' | 'panel' | 'bar';
  onDone?: () => void;
}) {
  const { data: prefs } = useNotificationPrefs();
  const { nameOf } = useUsers();

  /** A hivo kozben letette - a gomboknak el kell tunniuk. */
  const [cancelled, setCancelled] = useState(false);
  /** Telefonhivasnal: egy masik kolléga vette fel. */
  const [answeredElsewhere, setAnsweredElsewhere] = useState(false);
  /** MI vettuk fel, csak egy masik ablakban vagy eszkozon. */
  const [pickedUpElsewhere, setPickedUpElsewhere] = useState(false);

  /*
    A hivas allapotat KERDEZZUK, nem varjuk.

    Ez az ablak a fo alkalmazason KIVUL all, sajat gyoker-utvonalon: nincs benne
    valos ideju kapcsolat, tehat a "vege a hivasnak" esemenyrol nem ertesulne.
    Enelkul a hivo lerakhatja a telefont, a hivott meg mindig csorogne.

    Ezert rakerdezunk masodpercenkent, amig csorog. Ez tobb, mint amennyit egy
    esemeny kerulne - de az esemeny NEM jut el ide, es egy hivasnal a "miert
    csorog meg mindig?" a legrosszabb elerheto elmeny.
  */
  const { data: huddle, isSuccess } = useQuery({
    queryKey: ['incoming-call', channelId],
    queryFn: () => getChannelHuddle(channelId),
    refetchInterval: cancelled ? false : 1_500,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  /*
    A hivo a huddle inditoja - KIVEVE, ha mi magunk vagyunk az: a
    ProjectFlow-hivas a mi nevunkben nyit huddle-t, es ott a sajat nevunk
    hivna minket. Akkor a huddle cime a jelzes ("Hívás: Ügyfél (+36 …)").
  */
  const live =
    huddle && huddle.startedBy !== meId
      ? (huddle.participants.find((p) => p.user.id === huddle.startedBy)?.user ?? null)
      : null;
  const [ownTitle, setOwnTitle] = useState<string | null>(null);
  useEffect(() => {
    if (huddle?.startedBy === meId && huddle.title) setOwnTitle(huddle.title);
  }, [huddle?.startedBy, huddle?.title, meId]);

  /*
    TELEFONHIVAS: a hivonak nincs felhasznaloja - a szama es a vonal neve a
    jelzes. Ezt is megjegyezzuk, mert a hivas vegen a huddle-lel egyutt eltunik.
  */
  const phoneLeg = huddle?.phoneLegs.find((leg) => leg.direction === 'inbound') ?? null;
  const phoneContacts = usePhoneContacts();
  const [phoneCaller, setPhoneCaller] = useState<{ number: string; line: string | null } | null>(
    null,
  );
  useEffect(() => {
    if (phoneLeg) {
      setPhoneCaller({
        // A cimjegyzekbeli nevvel, ha van: "Kiss Anna (+36 ...)".
        number: phoneContacts.display(phoneLeg.callerNumber),
        line: phoneLeg.lineLabel,
      });
    }
  }, [phoneLeg?.callerNumber, phoneLeg?.lineLabel]); // eslint-disable-line react-hooks/exhaustive-deps

  /*
    Egy MASIK kolléga felvette: a szerver torolte a mi behivasunkat. A hivas
    el, de nekunk mar nem cseng - ezt kulon mondjuk ki, nem "megszakadt"-kent.
  */
  useEffect(() => {
    if (!huddle || cancelled) return;
    const me = huddle.participants.find((p) => p.user.id === meId);
    if (!me && huddle.phoneLegs.length > 0) {
      stopRingtone();
      setAnsweredElsewhere(true);
      setCancelled(true);
      return;
    }
    /*
      MI vettuk fel, de nem ebben az ablakban: egy masik bongeszoablakban, a
      telepitett alkalmazasban vagy a telefonon. Ez az ablak a felvetelkor
      eltunik, tehat ha mar bent vagyunk, az mashol tortent - itt nem
      csoroghet tovabb.
    */
    if (me?.state === 'joined') {
      stopRingtone();
      setPickedUpElsewhere(true);
      setCancelled(true);
      return;
    }

    /*
      A HIVO VISSZAVONTA A BEHIVAST, de a hivas maga tovabb el (o bent maradt masokkal).
      Mar nem vagyunk behivva - a szerver torolte a sorunkat -, ezert itt nem csorog
      tovabb: a letette allapotba kerulunk, mintha a hivas veget ert volna.
    */
    if (!me && huddle.phoneLegs.length === 0) {
      stopRingtone();
      setCancelled(true);
    }
  }, [huddle, meId, cancelled]);

  /*
    A hivo szemelyet MEGJEGYEZZUK.

    A hivas vegen a szerver mar nem ismeri a hivast, tehat a resztvevo-lista is
    eltunik vele - es epp akkor veszne el a nev, amikor ki kell irni, hogy KI
    tette le. A "Ismeretlen letette" nem informacio, hanem zaj.
  */
  const [caller, setCaller] = useState(live);
  useEffect(() => {
    if (live) setCaller(live);
  }, [live]);

  /**
   * A hivas megszunt: a szerver mar nem ismeri.
   *
   * Csak SIKERES valaszra dontunk. Egy halozati hiba is ures adatot ad, es
   * abbol "megszakadt"-at kiirni egy elo hivasra rosszabb, mint varni egyet -
   * a kovetkezo lekerdezes ugyis egy masodpercen belul jon.
   */
  useEffect(() => {
    if (!isSuccess || huddle || cancelled) return;
    stopRingtone();
    setCancelled(true);
  }, [huddle, isSuccess, cancelled]);

  /*
    A hang megprobal szolni.

    Egy frissen nyitott ablakban a bongeszo megtagadhatja a lejatszast, ha nem
    kozvetlen kattintasbol nyilt. Ezert a hangot NEM tekintjuk garantaltnak: a
    kepernyo maga es a rendszer-ertesites a megbizhato jelzes.
  */
  useEffect(() => {
    if (cancelled) return;
    startRingtone(prefs?.ringtone ?? DEFAULT_RINGTONE);
    return () => stopRingtone();
  }, [prefs?.ringtone, cancelled]);

  /*
    A kepernyo maga mar csorog: a rendszer-ertesites (push) ne szoljon melle.
    De CSAK ha latszik - a hatterben megnyilt hivas-kepernyot senki nem latja,
    ott eppen az ertesites a jelzes.
  */
  useEffect(() => {
    const clear = () => {
      if (document.visibilityState === 'visible') closeCallNotifications(channelId);
    };
    clear();
    document.addEventListener('visibilitychange', clear);
    return () => document.removeEventListener('visibilitychange', clear);
  }, [channelId]);

  // A megszakadt hivas ablaka magatol bezarodik - nincs mit tenni benne.
  useEffect(() => {
    if (!cancelled) return;
    closeCallNotifications(channelId);
    const timer = setTimeout(() => {
      if (variant !== 'window') onDone?.();
      else void closeHuddleWindow();
    }, 2_500);
    return () => clearTimeout(timer);
  }, [cancelled, channelId, variant, onDone]);

  /**
   * A hivas nem varhat orokke.
   *
   * Ha egy percig nem valaszolunk, elengedjuk. Egy ott ragadt "felveszed?"
   * kerdes rosszabb, mintha nem latszana.
   */
  useEffect(() => {
    if (cancelled) return;
    const timer = setTimeout(() => setCancelled(true), 60_000);
    return () => clearTimeout(timer);
  }, [cancelled]);

  /*
    Ha nem tudjuk, ki hiv, azt MEGMONDJUK - nem talalunk ki nevet.

    Ez akkor fordulhat elo, ha a hivas azelott szakad meg, hogy az elso valasz
    beerkezett volna. Az "Ismeretlen letette" nem informacio, hanem zaj.
  */
  const callerName = caller ? nameOf(caller) : (phoneCaller?.number ?? null);
  /** A sajat nevunkben nyitott hivas (ProjectFlow): a cime a fejlec, nem "X hív". */
  const heading = ownTitle ?? (callerName ? `${callerName} hív` : 'Bejövő ölelés');

  if (variant === 'bar') {
    return (
      <div className="flex min-w-0 items-center gap-2">
        <span
          aria-hidden
          className={`size-2.5 shrink-0 rounded-full bg-accent ${cancelled ? '' : 'animate-pulse'}`}
        />
        <p role="status" className="min-w-0 flex-1 truncate text-sm font-semibold">
          {cancelled
            ? pickedUpElsewhere
              ? 'Máshol felvetted'
              : answeredElsewhere
                ? 'Egy kolléga felvette'
                : 'A hívás megszakadt'
            : heading}
        </p>
        {!cancelled && (
          <>
            <button
              type="button"
              onClick={() => {
                stopRingtone();
                onAccept();
              }}
              className="flex shrink-0 items-center gap-1.5 rounded-control bg-accent px-2.5 py-1.5 text-xs font-semibold text-accent-ink hover:bg-accent-hover"
            >
              <Icon name="huddle" /> Felveszem
            </button>
            <button
              type="button"
              onClick={() => {
                stopRingtone();
                onDecline();
              }}
              aria-label="Elutasítom"
              title="Elutasítom"
              className="grid size-8 shrink-0 place-items-center rounded-control bg-[var(--danger)] text-white hover:brightness-110"
            >
              <Icon name="kilepes" />
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      className={
        variant === 'panel'
          ? 'grid place-items-center bg-surface p-3'
          : 'grid h-full place-items-center bg-surface p-6'
      }
    >
      <div className="w-full max-w-sm text-center">
        <div className="mx-auto mb-5 w-fit">
          {phoneCaller ? (
            <span
              aria-hidden
              className={`grid size-16 place-items-center rounded-2xl bg-accent-soft ${cancelled ? '' : 'animate-pulse'}`}
            >
              <Icon name="telefon" size="xl" />
            </span>
          ) : caller ? (
            <span className={cancelled ? '' : 'block animate-pulse'}>
              <Avatar
                userId={caller.id}
                name={caller.displayName}
                avatarUrl={caller.avatarUrl}
                size="lg"
              />
            </span>
          ) : (
            <span
              aria-hidden
              className="grid size-16 place-items-center rounded-2xl bg-accent-soft"
            >
              <Icon name="huddle" size="xl" />
            </span>
          )}
        </div>

        {/*
          A cim MINDKET allapotban ugyanabban az elo regioban all - igy a
          csengetesbol "megszakadt"-ra valtast a kepernyoolvaso is bemondja.
          Feltetelesen renderelt regio valtozasat nem mindig olvasna fel.
        */}
        <div role="status">
          <p className="break-words font-display text-xl font-semibold">
            {cancelled
              ? pickedUpElsewhere
                ? 'Máshol felvetted'
                : answeredElsewhere
                  ? 'Egy kolléga felvette'
                  : 'A hívás megszakadt'
              : heading}
          </p>
          <p className="mt-1 break-words text-sm text-ink-muted">
            {cancelled
              ? pickedUpElsewhere
                ? 'A hívás egy másik ablakodban vagy eszközödön folyik.'
                : answeredElsewhere
                  ? `${callerName ?? 'A hívót'} már más fogadta.`
                  : callerName
                    ? `${callerName} letette.`
                    : 'A hívó letette.'
              : ownTitle
                ? 'Felvétel után tárcsázzuk'
                : phoneCaller
                  ? `Telefonhívás${phoneCaller.line ? ` · ${phoneCaller.line}` : ''}`
                  : 'Ölelés'}
          </p>
        </div>

        {!cancelled && (
          <>
            {/*
              KI HIV? - a TT ugyfelkartyaja, ha telefonhivas.

              A gombok FOLOTT all, de a csengetes nem var ra: a kartya sajat
              kerdesbol tolt, es ha a TT nem valaszol, ez a resz marad ki - a
              "Felveszem" ugyanugy ott van, ugyanabban a pillanatban.

              CSAK OLVASHATO (`assignable` nelkul): egy ismeretlen szamot innen
              nem lehet ugyfelhez kotni. Csengeteskor a kolléga maga sem tudja
              meg, ki van a szam mogott - a hozzarendeles a felvetel utan, a
              hivas kozbeni savban jon elo.
            */}
            {phoneLeg?.callerNumber && (
              <div className="mt-4 text-left">
                <CustomerCard number={phoneLeg.callerNumber} compact />
              </div>
            )}

            <div className="mt-7 flex gap-2">
              <button
                type="button"
                autoFocus={variant !== 'panel'}
                onClick={() => {
                  stopRingtone();
                  onAccept();
                }}
                className="flex flex-1 items-center justify-center gap-2 rounded-control bg-accent px-3 py-3 text-sm font-semibold text-accent-ink transition-colors duration-(--duration-quick) hover:bg-accent-hover"
              >
                <Icon name="huddle" /> Felveszem
              </button>
              <button
                type="button"
                onClick={() => {
                  stopRingtone();
                  onDecline();
                }}
                className="flex flex-1 items-center justify-center gap-2 rounded-control bg-[var(--danger)] px-3 py-3 text-sm font-semibold text-white transition-transform duration-(--duration-quick) hover:brightness-110 active:scale-95"
              >
                <Icon name="kilepes" /> Elutasítom
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
